import React, { useMemo, forwardRef, ReactNode, useEffect } from 'react';

import { Box } from '@vimeo/bokeh';
import { useViewer } from '@vimeo/viewer';

import { useHelpPanel } from '../hooks/useHelpPanel';
import { initializeWidget } from '../utils/widget_actions';
import { ArticleListPanel } from './ArticleListPanel';
import { ArticlePanel } from './ArticlePanel';
import { HomePanel } from './HomePanel';
import { PanelHeader } from './PanelHeader';
import { SearchPanel } from './SearchPanel';
import { SectionPanel } from './SectionPanel';

export const HelpPanel = forwardRef<HTMLDivElement, Record<string, unknown>>((props, ref) => {
  const viewer = useViewer();
  const { isPanelOpen, currentPage, history, data } = useHelpPanel();

  useEffect(() => {
    const runInitialization = async () => {
      try {
        await initializeWidget();
        console.log('Zendesk widget initialized and hidden, ready for widgetOpen call.');
      } catch (error) {
        console.error('Failed to initialize Zendesk widget:', error);
      }
    };

    runInitialization();
  }, []);

  const headerProps = useMemo(() => {
    const showBackButton = history.length > 0 && currentPage !== 'home';
    const showSearchAndContact = currentPage === 'home' || currentPage === 'search';
    const showViewRequestsButton = currentPage === 'home' && !!viewer?.user?.id;

    let title = '';
    switch (currentPage) {
      case 'home':
      case 'search':
        title = 'Help';
        break;
      case 'section':
        title = data?.section?.title || 'Section';
        break;
      case 'article':
        title = data?.article?.title || 'Article';
        break;
      case 'article_list':
        title = data?.article_list?.title || 'Article List';
        break;
      default:
        title = 'Help';
    }

    return {
      title,
      showBackButton,
      showSearchButton: showSearchAndContact,
      showContactUsMenu: showSearchAndContact,
      showViewRequestsButton,
    };
  }, [currentPage, history, data, viewer?.user?.id]);

  if (!isPanelOpen) {
    return null;
  }

  const renderPanelContent = (page: string): ReactNode => {
    switch (page) {
      case 'home':
        return <HomePanel />;
      case 'section':
        return <SectionPanel />;
      case 'article_list':
        return <ArticleListPanel />;
      case 'article':
        return <ArticlePanel />;
      case 'search':
        return <SearchPanel />;
      default:
        return <HomePanel />;
    }
  };

  return (
    <Box
      ref={ref}
      position="absolute"
      top="100%"
      right="lg"
      width="360px"
      display="flex"
      flexDirection="column"
      overflowY="auto"
      height="calc(100vh - 84px)"
      bg="var(--vimeo-colors-surface)"
      boxShadow="0px 0px 24px -5px rgba(0, 0, 0, 0.16), 0px 0px 4px 0px rgba(0, 0, 0, 0.08)"
      borderRadius="md"
      padding="md"
      gap="lg"
      isolation="isolate"
      fontFamily="body"
      backdropFilter="blur(12px)"
    >
      <PanelHeader {...headerProps} />
      <Box
        flex="1"
        overflowY="auto"
        sx={{
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {renderPanelContent(currentPage)}
      </Box>
    </Box>
  );
});
